import { useMemo } from 'react';

import { ia, iaRa } from 'lib/helpers/utility';
import { getAgGridColumns } from 'lib/helpers/pdf/tableExport/lib/getAgGridData';

import {
  generateColumnDefs,
  generateHeaderName
} from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';

const dedupeByField = (columns) =>
  Object.values(
    columns.reduce((acc, col) => {
      acc[col.field] = col;
      return acc;
    }, {})
  );

const isActionsColumn = (col) =>
  (col.headerName || '').toLowerCase() === 'actions' || col.field === 'actions';

const normalizeColumn = (col) => {
  const field = col.field || col.colId;

  return {
    ...col,
    field,
    headerName: col.headerName || generateHeaderName(String(field))
  };
};

const mergeWithDefaultColumns = (colDefs, defaultColumns) => {
  const current = iaRa(colDefs);
  const fieldsInUse = new Set(current.map((col) => col.field || col.colId));
  const headersInUse = new Set(current.map((col) => col.headerName).filter(Boolean));

  const missingColumns = iaRa(defaultColumns).filter(
    (col) => !fieldsInUse.has(col.field || col.colId) && !headersInUse.has(col.headerName)
  );

  return [...current, ...missingColumns];
};

export const useExportColumns = ({
  colDefs,
  defaultColumns,
  gridApi,
  exportColumnsKeys,
  printExpandedRows
}) => {
  const columns = useMemo(() => {
    const safeColDefs = mergeWithDefaultColumns(colDefs, defaultColumns);
    const keys = iaRa(exportColumnsKeys);

    const exportableColumns = safeColDefs
      .filter((col) => (col.field || col.colId) && !isActionsColumn(col))
      .map(normalizeColumn);

    if (keys.length && keys[0] !== 'all') {
      const filtered = exportableColumns.filter((col) => keys.includes(col.field));

      if (filtered.length) return filtered;
    }

    if (!ia(exportableColumns) && gridApi) return getAgGridColumns(gridApi, false);

    return dedupeByField(exportableColumns);
  }, [gridApi, colDefs, defaultColumns, exportColumnsKeys]);

  const childColumns = useMemo(() => {
    if (!printExpandedRows || !gridApi) return [];

    let children = [];
    gridApi.forEachNode((node) => {
      if (!children.length && node.data?.children?.length) children = node.data.children;
    });

    if (!ia(children)) return [];
    return generateColumnDefs(children);
  }, [gridApi, printExpandedRows]);

  return {
    columns,
    childColumns,
    hasExpandableRows: !!printExpandedRows && ia(childColumns)
  };
};
